import React, {useEffect} from 'react';

import {AnalysisTemplateSelectorTemplate} from 'analysis-tool/graphql/AnalysisTemplateSelectorQuery';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {SelectDropdownProps} from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {AnalysisTemplateGraphQL} from './AnalysisTemplateTypes';
import {GetAnalysisTemplate} from './graphql/__generated/GetAnalysisTemplate';
import {GET_ANALYSIS_TEMPLATE_QUERY} from './graphql/GetAnalysisTemplate';
import SimpleAnalysisTemplateSelector from './SimpleAnalysisTemplateSelector';
import {TemplateApplyStateType} from './useAnalysisTemplateSelectorState';

interface AnalysisTemplateSelectorProps {
  templateApplyState: TemplateApplyStateType;
  // SelectId has to be a part of global state or else when the global state is deleted, this component will
  // just repopulate the template.
  onSelectId: (selectedTemplateId: string | null) => void;
  onSelect: (selectedTemplate: AnalysisTemplateGraphQL | null) => void;
  selectDropdownProps?: Pick<SelectDropdownProps, 'dropdownToggleOverride'>;
  filterTemplate?: (template: AnalysisTemplateSelectorTemplate) => boolean;
  includeNone?: boolean;
}

/**
 * Renders a searchable dropdown menu for selecting from existing analysis templates.
 *
 * @param value - The currently selected analysis template.
 * @param onSelect - The callback to call when a template is selected from the dropdown options.
 * @param selectDropdownProps - Additional props to pass to the SelectDropdown component.
 * @param filterTemplate - An optional filter function to apply to the templates. If the function returns false, the template will not be displayed in the dropdown.
 * @param includeNone - An optional flag to include a "None" option in the dropdown.
 * @returns The rendered searchable dropdown menu.
 */
export default function AnalysisTemplateSelector({
  templateApplyState,
  onSelectId,
  onSelect,
  selectDropdownProps,
  filterTemplate,
  includeNone,
}: AnalysisTemplateSelectorProps): JSX.Element | null {
  const templateQueryResult = useSkippableBenchlingQuery<GetAnalysisTemplate>(GET_ANALYSIS_TEMPLATE_QUERY, {
    skip:
      templateApplyState.selectedTemplateId == null ||
      templateApplyState.selectedTemplateId === templateApplyState.selectedTemplate?.id,
    variables: {analysisTemplateId: templateApplyState.selectedTemplateId},
  });

  useEffect(() => {
    if (
      templateQueryResult.type === QueryResultType.SUCCESS &&
      !isInaccessible(templateQueryResult.data.analysisTemplate) &&
      templateApplyState.selectedTemplateId !== templateApplyState.selectedTemplate?.id
    ) {
      onSelect(templateQueryResult.data.analysisTemplate);
    }
  }, [templateApplyState, onSelect, templateQueryResult]);

  if (templateQueryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }

  return (
    <SimpleAnalysisTemplateSelector
      template={
        templateApplyState.selectedTemplate ??
        (templateApplyState.selectedTemplateId ? {id: templateApplyState.selectedTemplateId} : null)
      }
      onSelectTemplate={(analysisTemplate: AnalysisTemplateSelectorTemplate | null) => {
        if (analysisTemplate == null) {
          onSelectId(null);
          onSelect(null);
          return;
        }
        onSelectId(analysisTemplate.id);
      }}
      dropdownToggleOverride={selectDropdownProps?.dropdownToggleOverride}
      filterTemplate={filterTemplate}
      includeNone={includeNone}
    />
  );
}
